import { useQueryClient } from '@tanstack/react-query';
import { useSmallFileUpload } from './useSmallFileUpload';
import { ExtendedUploadFileInfo } from '../../../../Model/ExtendedUploadFileInfo';
import { useValidateMetadata } from '../../../../context/documents/useValidateMetadata';
import { useUpdateDocumentMetadata } from '../../../../context/documents/useUpdateDocumentMetadata';
import { useChunkedUpload } from './useChunkUpload';
import { GearDocumentMetadata } from '../../../../Model/GearDocumentMetadata';
import { ValidateMetadataRequest } from '../../../../Model/requests/ValidateMetadataRequest';
import { PERMISSIONS_TO_VALIDATE_FOR } from '../../../../types/GearFormProps';
import { CONTENT_TYPE_FIELD, DOC_ID_FIELD, FILE_FIELD } from '../../../../config/consts';
import { UpdateDocumentMetadata } from '../../../../Model/requests/UpdateDocumentMetadata';
import { documentsKeys } from '../../../../context/documents/queryKeyFactory';

interface UseDocumentSubmissionParams {
  indexId: number;
  locale: string;
  isNewDocument: boolean;
  contentTypeFields: any[];
  allowsMultiFileUpload: boolean;
  existingDocument?: any;
  existingDocumentETag?: string;
  existingDocumentFileName?: string;
}

interface FileWithMetadata {
  file?: ExtendedUploadFileInfo;
  perFileMetadata?: Record<string, any>;
}

export const useDocumentSubmission = ({
  indexId,
  locale,
  isNewDocument,
  contentTypeFields,
  allowsMultiFileUpload,
  existingDocument,
  existingDocumentETag,
  existingDocumentFileName
}: UseDocumentSubmissionParams) => {
  const queryClient = useQueryClient();
  const { mutateAsync: validateMetadata } = useValidateMetadata();
  const { mutate: mutateUpdateDocumentMetadata } = useUpdateDocumentMetadata();
  const { uploadSmallFile, isLoading: isSmallFileUploading } = useSmallFileUpload({ isNewDocument });
  const {
    uploadLargeFile,
    isLargeFile,
    isLoading: isChunkedUploading
  } = useChunkedUpload({ selectedIndexId: indexId, isNewDocument });

  const extractMetadata = (dataItem: any): GearDocumentMetadata => {
    return Object.keys(dataItem)
      .filter(key => contentTypeFields.some(f => f.name === key))
      .reduce((obj: GearDocumentMetadata, key) => {
        obj[key] = dataItem[key];
        return obj;
      }, {});
  };

  const createValidateDto = (
    dataItem: any,
    metadata: GearDocumentMetadata,
    docId?: string
  ): ValidateMetadataRequest => {
    return {
      docId,
      metadata,
      indexId,
      validateFor: isNewDocument ? PERMISSIONS_TO_VALIDATE_FOR.CREATE : PERMISSIONS_TO_VALIDATE_FOR.UPDATE,
      ctName: isNewDocument ? dataItem[CONTENT_TYPE_FIELD].name : existingDocument[CONTENT_TYPE_FIELD],
      locale
    };
  };

  const getFileFromData = (dataItem: any): File | undefined => {
    if (isNewDocument) {
      return dataItem.file?.[0]?.getRawFile?.();
    }
    return dataItem[FILE_FIELD]?.[0]?.getRawFile?.();
  };

  const handleMetadataOnlyUpdate = (metadata: GearDocumentMetadata, onSuccess?: () => void): void => {
    if (!isNewDocument && existingDocument && existingDocumentFileName) {
      const metadataDto: UpdateDocumentMetadata = {
        ctName: existingDocument[CONTENT_TYPE_FIELD],
        documentId: existingDocument[DOC_ID_FIELD],
        fileName: existingDocumentFileName,
        searchIndexId: indexId,
        metadata
      };

      mutateUpdateDocumentMetadata(
        { dto: metadataDto, etag: existingDocumentETag },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries({
              queryKey: documentsKeys.documentMetadata({
                documentId: existingDocument[DOC_ID_FIELD],
                ctName: existingDocument[CONTENT_TYPE_FIELD],
                indexId
              })
            });
            onSuccess?.();
          }
        }
      );
    }
  };

  const handleSingleFileSubmission = async (
    dataItem: any,
    metadata: GearDocumentMetadata,
    onSuccess?: () => void,
    retry = false
  ): Promise<void> => {
    const file = getFileFromData(dataItem);
    const ctName = isNewDocument ? dataItem[CONTENT_TYPE_FIELD].name : existingDocument[CONTENT_TYPE_FIELD];

    if (!file) {
      handleMetadataOnlyUpdate(metadata, onSuccess);
      return;
    }

    if (isLargeFile(file.size)) {
      await uploadLargeFile({
        file,
        ctName,
        documentId: isNewDocument ? undefined : existingDocument[DOC_ID_FIELD],
        metadata
      });

      await queryClient.invalidateQueries({
        queryKey: documentsKeys.documentMetadata({
          documentId: isNewDocument ? dataItem[DOC_ID_FIELD] : existingDocument[DOC_ID_FIELD],
          ctName,
          indexId
        })
      });

      onSuccess?.();
    } else {
      uploadSmallFile(
        {
          file,
          ctName,
          documentId: isNewDocument ? undefined : existingDocument[DOC_ID_FIELD],
          metadata,
          etag: existingDocumentETag,
          retry
        },
        {
          onSuccess: async () => {
            if (!isNewDocument) {
              await queryClient.invalidateQueries({
                queryKey: documentsKeys.documentMetadata({
                  documentId: existingDocument[DOC_ID_FIELD],
                  ctName: existingDocument[CONTENT_TYPE_FIELD],
                  indexId
                })
              });
            }
            onSuccess?.();
          }
        }
      );
    }
  };

  const validateSingleFileMetadata = async (combinedMetadata: GearDocumentMetadata, ctName: string): Promise<any[]> => {
    const validateDto: ValidateMetadataRequest = {
      docId: undefined,
      metadata: combinedMetadata,
      indexId,
      validateFor: PERMISSIONS_TO_VALIDATE_FOR.CREATE,
      ctName,
      locale
    };

    try {
      return await validateMetadata({ dto: validateDto });
    } catch {
      return [{ field: 'validation', message: 'Validation request failed' }];
    }
  };

  const handleMultipleFileSubmissions = async (
    filesWithMetadata: FileWithMetadata[],
    sharedMetadata: GearDocumentMetadata,
    ctName: string,
    onSuccess?: () => void,
    retry = false
  ): Promise<void> => {
    for (const fileWithMeta of filesWithMetadata) {
      const file = fileWithMeta.file?.getRawFile?.();
      if (!file) {
        continue;
      }

      const combinedMetadata = {
        ...sharedMetadata,
        ...fileWithMeta.perFileMetadata
      };

      if (isLargeFile(file.size)) {
        await uploadLargeFile({
          file,
          ctName,
          metadata: combinedMetadata
        });
      } else {
        uploadSmallFile({
          file,
          ctName,
          metadata: combinedMetadata,
          retry
        });
      }
    }

    onSuccess?.();
  };

  const submitForm = async (dataItem: any, onSuccess?: () => void, retry = false): Promise<void> => {
    const sharedMetadata = extractMetadata(dataItem);
    const isMultiFileCreation = isNewDocument && allowsMultiFileUpload;

    if (isMultiFileCreation) {
      const filesWithMetadata: FileWithMetadata[] = dataItem[FILE_FIELD];
      const ctName = dataItem[CONTENT_TYPE_FIELD].name;

      const validationPromises = filesWithMetadata.map(fileWithMeta => {
        const combinedMetadata = {
          ...sharedMetadata,
          ...fileWithMeta.perFileMetadata
        };
        return validateSingleFileMetadata(combinedMetadata, ctName);
      });

      const allValidationResults = await Promise.all(validationPromises);
      const hasErrors = allValidationResults.some(errors => errors.length > 0);

      if (hasErrors) {
        return;
      }

      await handleMultipleFileSubmissions(filesWithMetadata, sharedMetadata, ctName, onSuccess, retry);
    } else {
      const fileArray = dataItem[FILE_FIELD];
      const perFileMetadata = fileArray?.[0]?.perFileMetadata || {};
      const finalMetadata = {
        ...sharedMetadata,
        ...perFileMetadata
      };

      const validateDto = createValidateDto(
        dataItem,
        finalMetadata,
        isNewDocument ? undefined : existingDocument[DOC_ID_FIELD]
      );

      validateMetadata(
        { dto: validateDto },
        {
          onSuccess: async validationErrors => {
            if (validationErrors.length > 0) {
              return;
            }

            await handleSingleFileSubmission(dataItem, finalMetadata, onSuccess, retry);
          }
        }
      );
    }
  };

  return {
    submitForm,
    isLoading: isSmallFileUploading || isChunkedUploading
  };
};
